import React, { useState } from 'react';
import Table from '@components/table';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import moment from 'moment/moment';
import { useNavigate } from 'react-router-dom';
import { routes } from '@const/routes';
import { useAmlCases } from '@hooks/api/useAmlCases';
import { TAmlCaseLite } from '@xeppt/xeppt-sdk/types';
import Status from '@components/common/status';
import { getAmlCaseStatusColor } from '@utils/index';
import Button from '@components/common/button';
import Drawer from '@components/drawer';
import Select from '@components/common/select';
import RangeDatePicker from '@components/range_date_picker';

const caseStatusItems = [
    { value: '', label: 'All' },
    { value: 'open', label: 'Open' },
    { value: 'pending_filing', label: 'Pending Filing' },
    { value: 'filed', label: 'Filed' },
    { value: 'closed', label: 'Closed' },
    { value: 'customer_contacted', label: 'Customer Contacted' },
    { value: 'confirmed_fraud', label: 'Confirmed Fraud' },
    { value: 'confirmed_legitimate', label: 'Confirmed Legitimate' },
    { value: 'chargeback_filed', label: 'Chargeback Filed' }
];

const caseTypeItems = [
    { value: '', label: 'All' },
    { value: 'aml', label: 'AML' },
    { value: 'fraud', label: 'Fraud' },
    { value: 'dual', label: 'Dual' }
];

const orderItems = [
    { value: 'desc', label: 'Newest first' },
    { value: 'asc', label: 'Oldest first' }
];

const AmlCasesLayout = () => {
    const { cases, casesTotal, casesFilter, refetchCases, handleChangeCasesFilter } =
        useAmlCases({ isInitialCases: true });
    const navigate = useNavigate();
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const columns = [
        {
            label: 'ID',
            key: 'id'
        },
        {
            label: 'Status',
            key: 'status',
            render: (value: string) => (
                <Status variant={getAmlCaseStatusColor(value)}>{value || '-'}</Status>
            )
        },
        {
            label: 'Party',
            key: 'party_id'
        },
        {
            label: 'Alerts',
            key: 'alert_count'
        },
        {
            label: 'Opened',
            key: 'opened_at',
            render: (value: string) =>
                value ? moment(value).format('MM.DD.YYYY HH:mm:ss') : '-'
        },
        {
            label: 'Due',
            key: 'due_at',
            render: (value: string) =>
                value ? moment(value).format('MM.DD.YYYY HH:mm:ss') : '-'
        },
        {
            label: 'Overdue',
            key: 'overdue',
            render: (_: boolean, data: TAmlCaseLite) => (
                <Status variant={data.overdue ? 'error' : 'gray'}>
                    {data.overdue ? 'Yes' : 'No'}
                </Status>
            )
        },
        {
            label: 'Detail',
            key: 'id',
            render: (value: string) => (
                <button
                    className="link"
                    onClick={() => navigate(`${routes.aml_cases}/${value}`)}>
                    View
                </button>
            )
        }
    ];

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div className={styles.text_wrapper}>
                    <Typography variant="h4">AML Cases</Typography>
                    <Typography>{casesTotal} records found</Typography>
                </div>
                <div className={styles.actions}>
                    <Button
                        size="medium"
                        variant="primary"
                        leftIcon="filter_bars"
                        onClick={() => setIsFilterOpen(true)}>
                        Filter
                    </Button>
                </div>
            </div>
            <Table
                //@ts-ignore
                columns={columns}
                rows={cases}
                totalRows={casesTotal}
                onLoadData={() => refetchCases({ isNew: false, filter: casesFilter })}
                emptyDescription="Cases list is empty"
            />
            <Drawer
                className={styles.drawer}
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}>
                <Typography className={styles.title} variant="h5">
                    Filter
                </Typography>
                <Select
                    full
                    label="Status"
                    value={casesFilter.status ?? ''}
                    items={caseStatusItems}
                    onChange={(val) =>
                        handleChangeCasesFilter({
                            status: val ? (val as typeof casesFilter.status) : undefined
                        })
                    }
                />
                <Select
                    full
                    label="Case Type"
                    value={casesFilter.caseType ?? ''}
                    items={caseTypeItems}
                    onChange={(val) =>
                        handleChangeCasesFilter({
                            caseType: val ? (val as typeof casesFilter.caseType) : undefined
                        })
                    }
                />
                <RangeDatePicker
                    full
                    label="Date range"
                    values={[casesFilter.from || null, casesFilter.to || null]}
                    onChange={(val) =>
                        handleChangeCasesFilter({
                            from: val?.[0] ? new Date(val[0]).toISOString() : undefined,
                            to: val?.[1] ? new Date(val[1]).toISOString() : undefined
                        })
                    }
                    isDisableFeature
                />
                <Select
                    full
                    label="Sort order"
                    value={casesFilter.order}
                    items={orderItems}
                    onChange={(val) =>
                        handleChangeCasesFilter({ order: val as 'asc' | 'desc' })
                    }
                />
            </Drawer>
        </div>
    );
};

export default AmlCasesLayout;
